import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppServices, useUploadImage } from '../../../hook/services';
import { Icon } from '@iconify/react';

import {
  DEFAULT_THEME,
  BG_PRESETS,
  SECTIONS,
  getNestedValue,
  setNestedValue,
} from './themeConfig';
import { generatePreviewCss, generateProductionCss } from './cssGenerator';

const CDN_BASE = 'https://script.crmgenix.com/webhook/login_page_css';

/* ================================================================
   Tiny UI primitives
   ================================================================ */

const isHex = (v) => /^#([0-9a-f]{3,8})$/i.test(v?.trim?.());

const ColorInput = ({ value, onChange }) => (
  <div className="d-flex align-items-center gap-2">
    <input
      type="text"
      className="form-control form-control-sm radius-8"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="#6366f1"
    />
    {isHex(value) && (
      <input
        type="color"
        value={value.trim()}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: 36, height: 36, borderRadius: 8,
          border: '1px solid #dee2e6', padding: 2,
          cursor: 'pointer', flexShrink: 0,
        }}
      />
    )}
  </div>
);

const SelectInput = ({ value, onChange, options }) => (
  <select
    className="form-select form-select-sm radius-8"
    value={value}
    onChange={(e) => onChange(e.target.value)}
  >
    {options.map((o) => (
      <option key={o} value={o}>{o}</option>
    ))}
  </select>
);

const ImageUploader = ({ value, onUrlChange, onFile, label, inputId }) => (
  <div>
    {value && (
      <div className="mb-8 radius-8 border overflow-hidden" style={{ height: 120 }}>
        <img src={value} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      </div>
    )}
    <label
      htmlFor={inputId}
      className="d-flex align-items-center justify-content-center gap-2 radius-8 border py-10 px-12 mb-8"
      style={{ cursor: 'pointer', background: '#f8fafc', borderStyle: 'dashed', fontSize: 13 }}
    >
      <Icon icon="solar:cloud-upload-bold" className="text-primary-600" />
      <span className="fw-semibold text-secondary-light">Upload {label}</span>
    </label>
    <input type="file" accept="image/*" id={inputId} className="d-none" onChange={(e) => onFile(e.target.files?.[0])} />
    <input
      type="text"
      className="form-control form-control-sm radius-8"
      placeholder="Or paste image URL"
      value={value}
      onChange={(e) => onUrlChange(e.target.value)}
    />
  </div>
);

const POSITION_OPTIONS = [
  {
    value: 'left',
    label: 'Left',
    icon: 'solar:align-left-bold',
    cardLeft: '12%',
  },
  {
    value: 'center',
    label: 'Center',
    icon: 'solar:align-horizontal-center-bold',
    cardLeft: '50%',
  },
  {
    value: 'right',
    label: 'Right',
    icon: 'solar:align-right-bold',
    cardLeft: '88%',
  },
];

const PositionPicker = ({ value, onChange }) => (
  <div className="d-flex gap-12 justify-content-center flex-wrap">
    {POSITION_OPTIONS.map((opt) => {
      const isActive = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className="border-0 bg-transparent p-0"
          style={{ cursor: 'pointer', width: 140 }}
        >
          {/* Mini preview card */}
          <div
            className="radius-12 border overflow-hidden"
            style={{
              height: 90,
              position: 'relative',
              background: isActive
                ? 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)'
                : '#f8fafc',
              border: isActive ? '2px solid #6366f1' : '2px solid #e5e7eb',
              transition: 'all 0.2s ease',
            }}
          >
            {/* Simulated background */}
            <div style={{
              position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
              background: isActive
                ? 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.08) 100%)'
                : 'transparent',
            }} />
            {/* Simulated card */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: opt.cardLeft,
                transform: 'translate(-50%, -50%)',
                width: 36,
                height: 50,
                borderRadius: 6,
                background: isActive ? '#6366f1' : '#cbd5e1',
                boxShadow: isActive ? '0 2px 8px rgba(99,102,241,0.4)' : '0 1px 3px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
              }}
            />
          </div>
          {/* Label */}
          <div className="d-flex align-items-center justify-content-center gap-4 mt-8">
            <Icon
              icon={opt.icon}
              style={{
                fontSize: 16,
                color: isActive ? '#6366f1' : '#9ca3af',
                transition: 'color 0.2s ease',
              }}
            />
            <span
              className="text-xs fw-semibold"
              style={{
                color: isActive ? '#6366f1' : '#6b7280',
                transition: 'color 0.2s ease',
              }}
            >
              {opt.label}
            </span>
          </div>
        </button>
      );
    })}
  </div>
);

/* ================================================================
   Fullscreen Preview Overlay
   ================================================================ */

const FullscreenPreview = ({ previewCss, onClose }) => {
  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        background: '#000',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px',
          background: 'rgba(0,0,0,0.85)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          zIndex: 2,
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon icon="solar:eye-bold" style={{ color: '#10b981', fontSize: 18 }} />
          <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>Full Page Preview</span>
          <span style={{
            background: 'rgba(16,185,129,0.15)',
            color: '#10b981',
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 10,
          }}>
            LIVE
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>Press ESC to close</span>
          <button
            type="button"
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 14px',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.1)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239,68,68,0.8)';
              e.currentTarget.style.borderColor = 'rgba(239,68,68,0.8)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
            }}
          >
            <Icon icon="solar:close-circle-bold" style={{ fontSize: 16 }} />
            Close
          </button>
        </div>
      </div>

      {/* Preview area */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <style>{previewCss}</style>
        <div className="lp-preview" style={{ width: '100%', height: '100%' }}>
          <div className="flex v2-open sidebar-v2-agency" style={{ height: '100%' }}>
            <section className="hl_login" style={{ minHeight: '100%' }}>
              <div className="hl_login--header">
                <div className="container-fluid" style={{ minHeight: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 12px' }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>&#9650; HighLevel</span>
                  <div className="language-dropdown-container" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span className="language-label" style={{ fontSize: 11 }}>Language:</span>
                    <select style={{ fontSize: 11, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 4 }} defaultValue="en-US" disabled>
                      <option>English</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="hl_login--body">
                <div className="container-fluid">
                  <div className="card">
                    <div className="card-body">
                      <div className="login-card-heading">
                        <h2 className="heading2">Sign into your account</h2>
                      </div>

                      <div className="form-group">
                        <div className="hl-text-input-container">
                          <div className="flex space-x-3">
                            <span className="hl-text-input-label block text-sm font-medium text-gray-700 mb-1">Email</span>
                          </div>
                          <div className="relative rounded-md shadow-sm">
                            <input
                              type="text"
                              className="hl-text-input shadow-sm block w-full sm:text-sm border-gray-300 rounded text-gray-800"
                              placeholder="Your email address"
                              id="email"
                              defaultValue="user@example.com"
                              readOnly
                            />
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <div className="hl-text-input-container">
                          <div className="flex space-x-3">
                            <span className="hl-text-input-label block text-sm font-medium text-gray-700 mb-1">Password</span>
                          </div>
                          <div className="relative rounded-md shadow-sm">
                            <input
                              type="password"
                              className="hl-text-input shadow-sm block w-full sm:text-sm border-gray-300 rounded text-gray-800"
                              placeholder="The password you picked"
                              id="password"
                              defaultValue="password123"
                              readOnly
                            />
                            <button
                              type="button"
                              aria-label="Show password"
                              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#9ca3af' }}
                            >
                              &#128065;
                            </button>
                          </div>
                        </div>
                        <a href="#" className="forgot-password font-semibold text-curious-blue-500" id="forgot_passowrd_btn" onClick={(e) => e.preventDefault()}>
                          Forgot password?
                        </a>
                      </div>

                      <div className="form-group button-holder">
                        <div style={{ display: 'inline-block', position: 'relative', width: '100%' }}>
                          <button type="button" className="hl-btn justify-center w-full inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded text-white bg-curious-blue-500">
                            Sign in
                          </button>
                        </div>
                      </div>

                      <div className="mt-6" style={{ marginTop: 20 }}>
                        <div className="relative">
                          <div className="flex absolute inset-0 items-center" style={{ position: 'relative' }}>
                            <div className="w-full border-t border-gray-300" style={{ borderTop: '1px solid var(--lp-divider, #d1d5db)', width: '100%' }} />
                          </div>
                          <div className="relative flex justify-center text-sm" style={{ textAlign: 'center', marginTop: -10 }}>
                            <span className="px-2 text-gray-500 bg-white" style={{ padding: '0 10px', position: 'relative', zIndex: 1 }}>
                              Or Continue with
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 button-holder" style={{ marginTop: 12 }}>
                        <div id="g_id_signin" className="g_id_signin" style={{
                          border: '1px solid var(--lp-g-border, #d1d5db)',
                          borderRadius: 'var(--lp-g-radius, 8px)',
                          padding: 'var(--lp-g-padding, 10px 16px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 10,
                          cursor: 'pointer',
                          background: 'var(--lp-g-bg, #fff)',
                          fontSize: 'var(--lp-g-fs, 14px)',
                          fontWeight: 'var(--lp-g-fw, 500)',
                          color: 'var(--lp-g-color, #374151)',
                        }}>
                          <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/></svg>
                          Sign in with Google
                        </div>
                      </div>

                      <p className="foot-note" style={{ marginTop: 16, textAlign: 'center' }}>
                        By signing in you agree to our{' '}
                        <a href="#" className="font-semibold text-curious-blue-500" onClick={(e) => e.preventDefault()}>
                          Terms and Conditions
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   Main Component
   ================================================================ */

const LoginPageBuilder = () => {
  const AppService = useAppServices();
  const UploadImage = useUploadImage();
  const { companyId } = useParams();

  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('formPosition');
  const [cdnCopied, setCdnCopied] = useState(false);
  const [fullPreview, setFullPreview] = useState(false);

  const previewCss = useMemo(() => generatePreviewCss(theme, '.lp-preview'), [theme]);
  const productionCss = useMemo(() => generateProductionCss(theme), [theme]);

  const get = useCallback((path) => getNestedValue(theme, path) ?? '', [theme]);
  const set = useCallback((path, value) => setTheme((prev) => setNestedValue(prev, path, value)), []);

  /* ── Load existing record ───────────────────────────────── */
  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { response } = await AppService.LoginPageBuilder.filter({
          query: `company_id=${companyId}`,
        });
        if (cancelled) return;
        const existing = response?.data;
        if (existing?.theme_config && typeof existing.theme_config === 'object') {
          // Full restore: merge saved config over defaults so new fields get defaults
          const restored = deepMerge(DEFAULT_THEME, existing.theme_config);
          setTheme(restored);
        } else if (existing) {
          // Legacy record (saved before theme_config existed) — restore what we can
          setTheme((prev) => ({
            ...prev,
            branding: {
              ...prev.branding,
              logoUrl: existing.logo || '',
              backgroundImage: existing.background_image || '',
            },
          }));
        }
      } catch { /* first visit — no record */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  /* ── Save ────────────────────────────────────────────────── */
  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const payload = {
        company_id: companyId,
        css: productionCss,
        logo: theme.branding.logoUrl,
        background_image: theme.branding.backgroundImage,
        theme_config: theme,
      };
      const result = await AppService.LoginPageBuilder.create({ payload, toaster: true });
      if (result?.response?.success) {
        setStatus({ ok: true, msg: 'Saved successfully! CSS copied to clipboard.' });
        try { await navigator.clipboard.writeText(productionCss); } catch { /* */ }
      } else {
        setStatus({ ok: false, msg: result?.error?.message || 'Save failed.' });
      }
    } catch (err) {
      setStatus({ ok: false, msg: err?.message || 'Unexpected error.' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file, type) => {
    if (!file) return;
    try {
      const res = await UploadImage({ toaster: null, file, desiredPath: `login-page/${companyId || 'global'}` });
      const url = res?.response?.data;
      if (!url) return;
      if (type === 'logo') set('branding.logoUrl', url);
      else set('branding.backgroundImage', url);
    } catch { /* */ }
  };

  const handleReset = () => { setTheme(DEFAULT_THEME); setStatus(null); };

  const handleExportJson = () => {
    const blob = new Blob([JSON.stringify(theme, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `login-theme-${companyId || 'default'}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleImportJson = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        setTheme(deepMerge(DEFAULT_THEME, parsed));
        setStatus({ ok: true, msg: 'Theme imported successfully.' });
      } catch {
        setStatus({ ok: false, msg: 'Invalid JSON file.' });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleExportCss = () => {
    const blob = new Blob([productionCss], { type: 'text/css' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `login-theme-${companyId || 'default'}.css`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const copyCdn = async () => {
    try {
      await navigator.clipboard.writeText(`@import url("${CDN_BASE}/${companyId}");`);
      setCdnCopied(true);
      setTimeout(() => setCdnCopied(false), 2000);
    } catch { /* */ }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  const sectionKeys = Object.keys(SECTIONS);

  return (
    <div className="row gy-4">
      {/* ════════════ LEFT — Editor ════════════════════════════ */}
      <div className="col-xxl-8 col-lg-7">
        <div className="card h-100 p-0 radius-12">
          {/* Header */}
          <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex align-items-center gap-12">
              <div className="w-40-px h-40-px radius-8 bg-primary-100 d-flex align-items-center justify-content-center">
                <Icon icon="solar:pen-new-round-bold" className="text-primary-600 text-xl" />
              </div>
              <div>
                <h6 className="fw-bold mb-0">Login Page Builder</h6>
                <p className="text-secondary-light text-xs mb-0">Customize your GHL login page appearance</p>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* <label
                className="border bg-hover-primary-100 text-secondary-light text-sm px-12 py-8 radius-8 d-flex align-items-center gap-2 mb-0"
                style={{ cursor: 'pointer' }}
              >
                <Icon icon="solar:import-bold" />Import
                <input type="file" accept=".json" className="d-none" onChange={handleImportJson} />
              </label>
              <button type="button" className="border bg-hover-primary-100 text-secondary-light text-sm px-12 py-8 radius-8 d-flex align-items-center gap-2" onClick={handleExportJson}>
                <Icon icon="solar:export-bold" />JSON
              </button>
              <button type="button" className="border bg-hover-primary-100 text-secondary-light text-sm px-12 py-8 radius-8 d-flex align-items-center gap-2" onClick={handleExportCss}>
                <Icon icon="solar:document-text-bold" />CSS
              </button> */}
              <button type="button" className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-sm px-16 py-8 radius-8 d-flex align-items-center gap-2" onClick={handleReset}>
                <Icon icon="solar:restart-bold" />Reset
              </button>
              <button type="button" className="btn btn-primary text-sm px-20 py-9 radius-8 d-flex align-items-center gap-2" onClick={handleSave} disabled={saving}>
                {saving ? <><span className="spinner-border spinner-border-sm" />&nbsp;Saving...</> : <><Icon icon="solar:diskette-bold" />Save</>}
              </button>
            </div>
          </div>

          <div className="card-body p-24">
            {/* Status */}
            {status && (
              <div className={`alert ${status.ok ? 'alert-success' : 'alert-danger'} radius-8 d-flex align-items-center gap-2 mb-16 py-10`}>
                <Icon icon={status.ok ? 'solar:check-circle-bold' : 'solar:close-circle-bold'} className="text-xl flex-shrink-0" />
                {status.msg}
              </div>
            )}

            {/* CDN link */}
            {/* {companyId && (
              <div className="p-12 radius-8 mb-16 border d-flex align-items-center gap-2 flex-wrap" style={{ background: '#f8fafc' }}>
                <Icon icon="solar:link-bold" className="text-primary-600" />
                <code className="flex-grow-1 text-xs text-break" style={{ fontFamily: 'monospace', userSelect: 'all' }}>
                  {`@import url("${CDN_BASE}/${companyId}");`}
                </code>
                <button type="button" className="btn btn-sm btn-primary radius-6 px-12 py-4 d-flex align-items-center gap-1" onClick={copyCdn}>
                  <Icon icon={cdnCopied ? 'solar:check-circle-bold' : 'solar:copy-bold'} />
                  {cdnCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
            )} */}

            {/* Section tabs */}
            <div className="d-flex gap-2 mb-20 flex-wrap">
              {sectionKeys.map((key) => {
                const sec = SECTIONS[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveSection(key)}
                    className={`d-flex align-items-center gap-1 px-12 py-8 radius-8 border-0 text-xs fw-semibold ${
                      activeSection === key ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-secondary-light'
                    }`}
                    style={{ cursor: 'pointer', transition: 'all .15s' }}
                  >
                    <Icon icon={sec.icon} />
                    {sec.label}
                  </button>
                );
              })}
            </div>

            {/* Active section fields */}
            <div className="row gy-3">
              {SECTIONS[activeSection]?.fields.map((field) => (
                <FieldRenderer
                  key={field.key}
                  field={field}
                  value={get(field.key)}
                  onChange={(v) => set(field.key, v)}
                  onUpload={handleUpload}
                  bgPresets={BG_PRESETS}
                  currentBg={get('colors.pageBackground')}
                  onBgPreset={(v) => set('colors.pageBackground', v)}
                />
              ))}
            </div>

            {/* Bottom bar */}
            <div className="d-flex align-items-center justify-content-end mt-24 pt-16 border-top gap-2">
              <button type="button" className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-sm px-20 py-9 radius-8" onClick={handleReset}>
                Reset
              </button>
              <button type="button" className="btn btn-primary text-sm px-24 py-10 radius-8 d-flex align-items-center gap-2" onClick={handleSave} disabled={saving}>
                {saving ? <><span className="spinner-border spinner-border-sm" />&nbsp;Saving...</> : <><Icon icon="solar:diskette-bold" />Save Changes</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════ RIGHT — Live Preview ═════════════════════ */}
      <div className="col-xxl-4 col-lg-5">
        <div className="card h-100 p-0 radius-12" style={{ position: 'sticky', top: 24 }}>
          <div className="card-header border-bottom bg-base py-12 px-20 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-8">
              <div className="w-32-px h-32-px radius-8 bg-success-100 d-flex align-items-center justify-content-center">
                <Icon icon="solar:eye-bold" className="text-success-600" />
              </div>
              <h6 className="fw-bold mb-0 text-sm">Live Preview</h6>
            </div>
            <div className="d-flex align-items-center gap-8">
              <button
                type="button"
                onClick={() => setFullPreview(true)}
                className="d-flex align-items-center gap-4 px-10 py-4 radius-8 border text-xs fw-semibold"
                style={{ cursor: 'pointer', background: '#f8fafc', color: '#374151', transition: 'all .15s' }}
              >
                <Icon icon="solar:full-screen-bold" style={{ fontSize: 14 }} />
                Full Page
              </button>
              <span className="d-flex align-items-center gap-4 px-8 py-2 radius-20 text-xs fw-semibold" style={{ background: '#d1fae5', color: '#065f46' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
                Live
              </span>
            </div>
          </div>

          <div className="card-body p-0 overflow-hidden">
            <style>{previewCss}</style>
            {/* ── Preview replicates the ACTUAL GHL login DOM structure ── */}
            <div className="lp-preview">
              <div className="flex v2-open sidebar-v2-agency">
                <section className="hl_login" style={{ minHeight: 500 }}>
                  {/* Header bar */}
                  <div className="hl_login--header">
                    <div className="container-fluid" style={{ minHeight: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 12px' }}>
                      <span style={{ fontSize: 12, fontWeight: 700 }}>&#9650; HighLevel</span>
                      <div className="language-dropdown-container" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span className="language-label" style={{ fontSize: 11 }}>Language:</span>
                        <select style={{ fontSize: 11, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 4 }} defaultValue="en-US" disabled>
                          <option>English</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Login body */}
                  <div className="hl_login--body">
                    <div className="container-fluid">
                      <div className="card">
                        <div className="card-body">
                          {/* Heading with logo */}
                          <div className="login-card-heading">
                            <h2 className="heading2">Sign into your account</h2>
                          </div>

                          {/* Email field */}
                          <div className="form-group">
                            <div className="hl-text-input-container">
                              <div className="flex space-x-3">
                                <span className="hl-text-input-label block text-sm font-medium text-gray-700 mb-1">Email</span>
                              </div>
                              <div className="relative rounded-md shadow-sm">
                                <input
                                  type="text"
                                  className="hl-text-input shadow-sm block w-full sm:text-sm border-gray-300 rounded text-gray-800"
                                  placeholder="Your email address"
                                  id="email"
                                  defaultValue="user@example.com"
                                  readOnly
                                />
                              </div>
                            </div>
                          </div>

                          {/* Password field */}
                          <div className="form-group">
                            <div className="hl-text-input-container">
                              <div className="flex space-x-3">
                                <span className="hl-text-input-label block text-sm font-medium text-gray-700 mb-1">Password</span>
                              </div>
                              <div className="relative rounded-md shadow-sm">
                                <input
                                  type="password"
                                  className="hl-text-input shadow-sm block w-full sm:text-sm border-gray-300 rounded text-gray-800"
                                  placeholder="The password you picked"
                                  id="password"
                                  defaultValue="password123"
                                  readOnly
                                />
                                <button
                                  type="button"
                                  className="absolute inset-y-0 right-0 pr-3 flex items-center justify-center bg-transparent border-none cursor-pointer"
                                  aria-label="Show password"
                                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#9ca3af' }}
                                >
                                  &#128065;
                                </button>
                              </div>
                            </div>
                            <a href="#" className="forgot-password font-semibold text-curious-blue-500" id="forgot_passowrd_btn" onClick={(e) => e.preventDefault()}>
                              Forgot password?
                            </a>
                          </div>

                          {/* Sign In button */}
                          <div className="form-group button-holder">
                            <div style={{ display: 'inline-block', position: 'relative', width: '100%' }}>
                              <button type="button" className="hl-btn justify-center w-full inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded text-white bg-curious-blue-500">
                                Sign in
                              </button>
                            </div>
                          </div>

                          {/* Divider */}
                          <div className="mt-6" style={{ marginTop: 20 }}>
                            <div className="relative">
                              <div className="flex absolute inset-0 items-center" style={{ position: 'relative' }}>
                                <div className="w-full border-t border-gray-300" style={{ borderTop: '1px solid var(--lp-divider, #d1d5db)', width: '100%' }} />
                              </div>
                              <div className="relative flex justify-center text-sm" style={{ textAlign: 'center', marginTop: -10 }}>
                                <span className="px-2 text-gray-500 bg-white" style={{ padding: '0 10px', position: 'relative', zIndex: 1 }}>
                                  Or Continue with
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Google button mock */}
                          <div className="mt-3 button-holder" style={{ marginTop: 12 }}>
                            <div id="g_id_signin" className="g_id_signin" style={{
                              border: `1px solid var(--lp-g-border, #d1d5db)`,
                              borderRadius: 'var(--lp-g-radius, 8px)',
                              padding: 'var(--lp-g-padding, 10px 16px)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 10,
                              cursor: 'pointer',
                              background: 'var(--lp-g-bg, #fff)',
                              fontSize: 'var(--lp-g-fs, 14px)',
                              fontWeight: 'var(--lp-g-fw, 500)',
                              color: 'var(--lp-g-color, #374151)',
                            }}>
                              <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/></svg>
                              Sign in with Google
                            </div>
                          </div>

                          {/* Footer */}
                          <p className="foot-note" style={{ marginTop: 16, textAlign: 'center' }}>
                            By signing in you agree to our{' '}
                            <a href="#" className="font-semibold text-curious-blue-500" onClick={(e) => e.preventDefault()}>
                              Terms and Conditions
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>

          <div className="card-footer border-top py-8 px-16 bg-base">
            <p className="text-secondary-light text-xs mb-0 d-flex align-items-center gap-4">
              <Icon icon="solar:shield-check-bold" className="text-primary-600" />
              Preview matches actual GHL login page structure
            </p>
          </div>
        </div>
      </div>

      {/* ════════════ Fullscreen Preview Overlay ══════════════ */}
      {fullPreview && (
        <FullscreenPreview
          previewCss={previewCss}
          onClose={() => setFullPreview(false)}
        />
      )}
    </div>
  );
};

/* ================================================================
   Field Renderer
   ================================================================ */

const FieldRenderer = ({ field, value, onChange, onUpload, bgPresets, currentBg, onBgPreset }) => {
  const colClass = field.type === 'textarea' || field.type === 'gradient' || field.type === 'image' || field.type === 'position-picker'
    ? 'col-12'
    : 'col-md-6';

  const inner = (() => {
    switch (field.type) {
      case 'position-picker':
        return <PositionPicker value={value} onChange={onChange} />;

      case 'color':
        return <ColorInput value={value} onChange={onChange} />;

      case 'text':
        return (
          <input
            type="text"
            className="form-control form-control-sm radius-8"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
          />
        );

      case 'select':
        return <SelectInput value={value} onChange={onChange} options={field.options} />;

      case 'textarea':
        return (
          <>
            <div className="p-10 radius-8 border mb-8" style={{ background: '#fffbeb', fontSize: 12 }}>
              <Icon icon="solar:info-circle-bold" className="text-warning-600 me-4" />
              CSS written here is appended last and overrides all generated styles.
            </div>
            <textarea
              className="form-control radius-8"
              rows={8}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={`/* Example */\n.hl_login .card {\n  background: #000;\n}`}
              style={{ fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}
            />
          </>
        );

      case 'gradient':
        return (
          <div>
            <div className="d-flex flex-wrap gap-6 mb-12">
              {bgPresets.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => onBgPreset(p.value)}
                  className="border-0 bg-transparent p-0 d-flex flex-column align-items-center gap-4"
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className="radius-6 border"
                    style={{
                      width: 40, height: 40, background: p.value,
                      outline: currentBg === p.value ? '3px solid #6366f1' : 'none',
                      outlineOffset: 2,
                    }}
                  />
                  <span style={{ fontSize: 10 }} className="text-secondary-light">{p.label}</span>
                </button>
              ))}
              <div className="d-flex flex-column align-items-center gap-4">
                <label className="radius-6 border d-flex align-items-center justify-content-center mb-0" style={{ width: 40, height: 40, cursor: 'pointer' }}>
                  <Icon icon="solar:add-circle-bold" className="text-secondary-light" />
                  <input type="color" className="d-none" onChange={(e) => onBgPreset(e.target.value)} />
                </label>
                <span style={{ fontSize: 10 }} className="text-secondary-light">Custom</span>
              </div>
            </div>
            <div className="radius-6 mb-8 border" style={{ height: 16, background: value || '#667eea' }} />
            <input
              type="text"
              className="form-control form-control-sm radius-8"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            />
          </div>
        );

      case 'image':
        return (
          <ImageUploader
            value={value}
            onUrlChange={onChange}
            onFile={(f) => onUpload(f, field.uploadKey)}
            label={field.label}
            inputId={`lpb-${field.key}`}
          />
        );

      default:
        return null;
    }
  })();

  return (
    <div className={colClass}>
      <label className="form-label fw-semibold text-secondary-light text-xs mb-6">{field.label}</label>
      {inner}
    </div>
  );
};

/* ================================================================
   Deep merge utility
   ================================================================ */

function deepMerge(target, source) {
  if (!source || typeof source !== 'object') return target;
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (
      source[key] && typeof source[key] === 'object' && !Array.isArray(source[key]) &&
      typeof target[key] === 'object' && target[key] !== null
    ) {
      result[key] = deepMerge(target[key], source[key]);
    } else {
      result[key] = source[key];
    }
  }
  return result;
}

export default LoginPageBuilder;
